/**
 * pages/accounts/PaymentMethodsPage.tsx
 *
 * Manage bank accounts and MoMo accounts.
 * Users can add new accounts and remove existing ones.
 */

import { useEffect, useState } from "react";
import {
  AddCircleOutline,
  BusinessOutline,
  CashOutline,
  CheckmarkCircle,
  PhonePortraitOutline,
  TrashOutline,
} from "@raresail/react-ionicons";
import styled from "styled-components";
import { alpha, IconButton } from "@mui/material";
import {
  PRIMARY_COLOR,
  primary5,
  primary10,
  PRIMARY_LIGHT,
} from "../../../constants/colors";
import { useTitle } from "../../../hooks/common/useTitle";
import useGet from "../../../hooks/common/useGet";
import useSet from "../../../hooks/common/useSet";
import {
  API_USER_BANK_ACCOUNTS,
  API_USER_MOMO_ACCOUNTS,
} from "../../../constants/apis";
import {
  ADD_BANK_ACCOUNT,
  ADD_MOMO_ACCOUNT,
} from "../../../constants/constants";
import { openModalForm } from "../../../utils/modalUtils";
import {
  AnimateChildren,
  Divider,
  EmptyView,
  Heading,
} from "../../elements/Elements";
import { SimpleLoader } from "../../common/Loader";
import { Button } from "../../common/Button";
import {
  reloadData,
} from "../../lists/ListHandler";
import {
  useGlobalContext,
} from "../../context/GlobalContext";
import { stringify } from "../../../utils/helpers";

export const PaymentMethodsPage = () => {
  useTitle("Payment Methods");

  const { loadData: loadBanks, data: banksRaw, loading: loadingBanks } =
    useGet();
  const { loadData: loadMomos, data: momosRaw, loading: loadingMomos } =
    useGet();
  const { uploadData: deleteAccount, submitting: deleting } = useSet();

  const context = useGlobalContext();

  const fetchBanks = () => loadBanks({ api: API_USER_BANK_ACCOUNTS });
  const fetchMomos = () => loadMomos({ api: API_USER_MOMO_ACCOUNTS });

  useEffect(() => {
    fetchBanks();
    fetchMomos();
  }, []);

  // Reload when new accounts are added via modal
  useEffect(() => {
    if (context?.reload_bank_accounts) fetchBanks();
  }, [context?.reload_bank_accounts]);

  useEffect(() => {
    if (context?.reload_momo_accounts) fetchMomos();
  }, [context?.reload_momo_accounts]);

  const banks = Array.isArray(banksRaw) ? banksRaw : banksRaw?.results || [];
  const momos = Array.isArray(momosRaw) ? momosRaw : momosRaw?.results || [];

  const handleDeleteBank = (id: string) => {
    if (!window.confirm("Remove this bank account?")) return;
    deleteAccount({
      api: API_USER_BANK_ACCOUNTS,
      id,
      method: "PATCH" as any,
      params: { is_active: false },
      successMessage: { message: "Bank account removed" },
      onSuccess: fetchBanks,
    });
  };

  const handleDeleteMomo = (id: string) => {
    if (!window.confirm("Remove this MoMo account?")) return;
    deleteAccount({
      api: API_USER_MOMO_ACCOUNTS,
      id,
      method: "PATCH" as any,
      params: { is_active: false },
      successMessage: { message: "MoMo account removed" },
      onSuccess: fetchMomos,
    });
  };

  return (
    <div className="p15 flexColumn gap15">
      <AnimateChildren>
        {/* ── Bank Accounts ──────────────────────────── */}
        <Section>
          <div className="flexSpaceCenter mb10">
            <Heading>Bank Accounts</Heading>
            <Button
              className="font12"
              style={{ height: 28, padding: "0 12px" }}
              onClick={() => openModalForm({ source: ADD_BANK_ACCOUNT })}
            >
              <span className="font14">+</span> Add
            </Button>
          </div>
          <Divider />

          {loadingBanks ? (
            <SimpleLoader />
          ) : banks.length === 0 ? (
            <EmptyState>
              <BusinessOutline fontSize={30} className="grey" />
              <div className="font12 grey">No bank accounts added</div>
              <div className="font11 grey">
                Add a bank account for withdrawals and disbursements
              </div>
            </EmptyState>
          ) : (
            <div className="flexColumn gap10 mt10">
              {banks.map((bank: any) => (
                <AccountCard key={bank.id}>
                  <AccountIconCircle type="bank">
                    <BusinessOutline fontSize={16} />
                  </AccountIconCircle>
                  <div className="flexGrow" style={{ minWidth: 0 }}>
                    <div className="font13 semiBold textDark">
                      {bank.bank_name || bank.bank_display_name}
                    </div>
                    <div className="font12 grey">
                      {bank.account_number} · {bank.account_name}
                    </div>
                    {bank.branch_name && (
                      <div className="font11 grey">{bank.branch_name}</div>
                    )}
                    <div className="flexNullCenter gap5 mt3">
                      {bank.is_default_for_withdrawals && (
                        <DefaultBadge>
                          <CheckmarkCircle fontSize={10} /> Default
                        </DefaultBadge>
                      )}
                    </div>
                  </div>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteBank(bank.id)}
                    disabled={deleting}
                    sx={{ color: "error.main", opacity: 0.6, "&:hover": { opacity: 1 } }}
                  >
                    <TrashOutline fontSize={16} />
                  </IconButton>
                </AccountCard>
              ))}
            </div>
          )}
        </Section>

        {/* ── MoMo Accounts ──────────────────────────── */}
        <Section>
          <div className="flexSpaceCenter mb10">
            <Heading>Mobile Money</Heading>
            <Button
              className="font12"
              style={{ height: 28, padding: "0 12px" }}
              onClick={() => openModalForm({ source: ADD_MOMO_ACCOUNT })}
            >
              <span className="font14">+</span> Add
            </Button>
          </div>
          <Divider />

          {loadingMomos ? (
            <SimpleLoader />
          ) : momos.length === 0 ? (
            <EmptyState>
              <PhonePortraitOutline fontSize={30} className="grey" />
              <div className="font12 grey">No MoMo accounts added</div>
              <div className="font11 grey">
                Add a mobile money number for deposits and withdrawals
              </div>
            </EmptyState>
          ) : (
            <div className="flexColumn gap10 mt10">
              {momos.map((momo: any) => (
                <AccountCard key={momo.id}>
                  <AccountIconCircle type="momo">
                    <PhonePortraitOutline fontSize={16} />
                  </AccountIconCircle>
                  <div className="flexGrow" style={{ minWidth: 0 }}>
                    <div className="font13 semiBold textDark">
                      {momo.phone_number}
                    </div>
                    <div className="font12 grey capitalize">
                      {stringify(momo.provider_display || momo.provider)} ·{" "}
                      {momo.account_name}
                    </div>
                    <div className="flexNullCenter gap5 mt3">
                      {momo.is_default && (
                        <DefaultBadge>
                          <CheckmarkCircle fontSize={10} /> Default
                        </DefaultBadge>
                      )}
                      {momo.is_verified && (
                        <VerifiedBadge>Verified</VerifiedBadge>
                      )}
                    </div>
                  </div>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteMomo(momo.id)}
                    disabled={deleting}
                    sx={{ color: "error.main", opacity: 0.6, "&:hover": { opacity: 1 } }}
                  >
                    <TrashOutline fontSize={16} />
                  </IconButton>
                </AccountCard>
              ))}
            </div>
          )}
        </Section>
      </AnimateChildren>
    </div>
  );
};

/* ─── Styles ────────────────────────────────────────────────────── */

const Section = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 16px;
`;

const EmptyState = styled.div`
  padding: 30px 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  text-align: center;
`;

const AccountCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px;
  border-radius: 12px;
  border: 1px solid ${PRIMARY_LIGHT};
  background: ${primary5};
  transition: border-color 0.15s;

  &:hover {
    border-color: ${alpha(PRIMARY_COLOR, 0.2)};
  }
`;

const AccountIconCircle = styled.div<{ type: "bank" | "momo" }>`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${({ type }) => (type === "bank" ? PRIMARY_COLOR : "#e65100")};
  background: ${({ type }) =>
    type === "bank" ? primary10 : alpha("#e65100", 0.1)};
`;

const DefaultBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 10px;
  color: ${PRIMARY_COLOR};
  background: ${primary10};
  padding: 2px 8px;
  border-radius: 12px;
  font-weight: 600;
`;

const VerifiedBadge = styled.div`
  font-size: 10px;
  color: #2e7d32;
  background: ${alpha("#2e7d32", 0.08)};
  padding: 2px 8px;
  border-radius: 12px;
  font-weight: 600;
`;